import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';

const TicketingHelpModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState([]);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!isOpen) setExpandedSections([]);
  }, [isOpen]);

  const toggleSection = (sectionId) => {
    const isExpanding = !expandedSections.includes(sectionId);
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    if (isExpanding) {
      setTimeout(() => {
        sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  };

  if (!isOpen) return null;

  const sections = [
    {
      id: 'creating-an-event',
      title: '1. Creating & Publishing an Event',
      content: (
        <>
          <p>Click <strong>"Create New Event Tickets"</strong> to open the 3-step wizard: Event Details, Ticket Setup, and Checkout.</p>
          <p>In <strong>Event Details</strong> you choose the event's timezone, so attendees always see the times in that zone.</p>
          <p>Clicking <strong>Next</strong> saves your progress as a draft automatically, so you can leave and come back at any time. Use <strong>"Load Existing Draft"</strong> to resume a draft you haven't published yet (it reopens on the step you left off).</p>
          <p>On the final step, click <strong>Publish Tickets</strong> to make the event live. Publishing also generates the check-in QR code. Once live, the event appears in public search, the calendar, and is bookable.</p>
        </>
      )
    },
    {
      id: 'editing-published',
      title: '2. Editing a Published (Live) Event',
      content: (
        <>
          <p><strong>Editing a live event never takes it offline.</strong> While you edit, the event stays visible and bookable, and buyers keep purchasing against the currently-published version.</p>
          <p>Your changes are kept as a private draft and do <strong>not</strong> go public until you re-publish. The hint at the top of the editor says: <em>"Changes remain in draft until you publish them in Step 3."</em></p>
          <p>Once you've actually changed something, the event's card on this page shows the status <strong>"Live - unpublished edits"</strong> (amber) instead of "Live", and the editor lists what you changed (e.g. <em>"Unpublished changes: Event details, Tickets"</em>) so you can see at a glance what's waiting to be published. Simply stepping through the wizard without editing anything does <strong>not</strong> flag the event. Re-open it and you'll resume with your parked changes intact.</p>
          <p><strong>To make the changes live:</strong> go to Step 3 and click <strong>Update Event</strong>. Your edits are applied in one action and the event never drops offline.</p>
          <p><strong>To throw the changes away:</strong> click <strong>Discard changes</strong> in the editor. The event reverts to its currently-published version and the card returns to plain "Live". (A fresh edit, with no parked changes, opens on Step 1.)</p>
        </>
      )
    },
    {
      id: 'ticket-quantities',
      title: '3. Ticket Quantities & Sold Limits',
      content: (
        <>
          <p>For a live event, you can raise a ticket type's quantity freely. You <strong>cannot</strong> reduce a quantity below the number already sold for that type. The quantity field is floored at the sold count, so the lowest you can set is what's already been purchased.</p>
          <p>You can still edit prices, names, descriptions, dates, and add new ticket types. As with all edits, changes stay in draft until you publish.</p>
        </>
      )
    },
    {
      id: 'qr-checkin',
      title: '4. Check-in QR Codes',
      content: (
        <>
          <p>There are two kinds of QR code for check-in:</p>
          <ul>
            <li><strong>Event QR code:</strong> generated when you publish. Click the <strong>QR</strong> button on the event card to display it, and show it prominently at the entrance. Attendees who have the Dance Localé mobile app scan this code to check themselves in.</li>
            <li><strong>Per-ticket QR codes:</strong> emailed to each ticket holder when they buy. If an attendee doesn't have the app, or prefers to show their emailed QR, you (the organiser or an admin) scan their individual ticket QR to check them in, one ticket at a time.</li>
          </ul>
        </>
      )
    },
    {
      id: 'cancelling',
      title: '5. Cancelling an Event',
      content: (
        <>
          <p>Use the calendar-remove icon on a live event card to cancel it. You'll see a preview of ticket holders and suggested refund amounts before confirming. Cancelling marks the event cancelled, refunds the selected purchasers, and notifies them by email.</p>
          <p>A cancelled event is removed from the public calendar and Find Events, so nobody discovers it as if it's still on. Anyone who follows a link they already have sees a clear "This event has been cancelled" page. It stays on this Manage page in its own section for your records.</p>
        </>
      )
    }
  ];

  return (
    <div className="classes-help-overlay" onClick={onClose}>
      <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
        <div className="classes-help-header">
          <div className="classes-help-header-content">
            <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
            <h2>Event Tickets Help</h2>
          </div>
          <button className="classes-help-close" onClick={onClose}>×</button>
        </div>

        <div className="classes-help-body">
          {sections.map(section => (
            <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
              <button
                className="help-accordion-header"
                onClick={() => toggleSection(section.id)}
              >
                <Icon
                  path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                  size={0.8}
                  color="var(--dl-color-purple-default)"
                />
                <span>{section.title}</span>
              </button>
              {expandedSections.includes(section.id) && (
                <div className="help-accordion-content">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="classes-help-footer">
          <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
        </div>
      </div>
    </div>
  );
};

export default TicketingHelpModal;
